import React, { useState, useEffect, useContext, useRef } from "react";
import { getCurrentReport, deleteExpenseLine, submitReport } from "../api";
import { ToastContext } from "../App";
import AddExpenseModal from "./AddExpenseModal";

const CATEGORY_ICONS = {
  Meals: "🍽️", Mileage: "🚗", Lodging: "🏨",
  Transportation: "🚌", Airfare: "✈️", "Office Supplies": "📦", Other: "🧾",
};

// Emails that can see the "submit on behalf of" dropdown.
const DELEGATE_EMAILS = ["dpoynter@lakeco.com"];

// People that can be submitted on behalf of.
const ON_BEHALF_OPTIONS = [
  { name: "Chris Orr", email: "corr@lakeco.com", label: "Chris Orr (CEO)" },
];

function formatCurrency(val) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr.slice(0, 10) + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export default function ExpenseReport({ user, triggerModal, reloadTrigger }) {
  const showToast = useContext(ToastContext);
  const [report, setReport]           = useState(null);
  const [lines, setLines]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showModal, setShowModal]     = useState(false);
  const [editLine, setEditLine]       = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [reportPurpose, setReportPurpose] = useState("");
  const [confirmDeleteId, setConfirmDeleteId]     = useState(null);
  const [onBehalfOf, setOnBehalfOf] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc"); // "asc" = oldest first, "desc" = newest first
  const deleteTimerRef = useRef(null);
  const lastTriggerModal = useRef(triggerModal);

  const isDelegate = DELEGATE_EMAILS.includes(user?.username?.toLowerCase());

  const load = async () => {
    try {
      setLoading(true);
      const data = await getCurrentReport();
      setReport(data.report || null);
      setLines(data.lines || []);
    } catch (e) {
      showToast("Failed to load expense report", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (reloadTrigger > 0) {
      setShowSubmitConfirm(false);
      setConfirmDeleteId(null);
      load();
    }
  }, [reloadTrigger]); // eslint-disable-line

  useEffect(() => {
    if (triggerModal > lastTriggerModal.current) {
      setEditLine(null);
      setShowModal(true);
    }
    lastTriggerModal.current = triggerModal;
  }, [triggerModal]); // eslint-disable-line

  useEffect(() => () => clearTimeout(deleteTimerRef.current), []);

  const handleDeleteTap = (lineId) => {
    if (confirmDeleteId === lineId) {
      clearTimeout(deleteTimerRef.current);
      setConfirmDeleteId(null);
      deleteExpenseLine(lineId)
        .then(() => {
          setLines((prev) => prev.filter((l) => l.ID !== lineId));
          showToast("Expense removed", "success");
        })
        .catch(() => showToast("Failed to remove expense", "error"));
    } else {
      setConfirmDeleteId(lineId);
      clearTimeout(deleteTimerRef.current);
      deleteTimerRef.current = setTimeout(() => setConfirmDeleteId(null), 3000);
    }
  };

  const handleEditTap = (line) => {
    setConfirmDeleteId(null);
    setEditLine(line);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!report) return;
    try {
      setSubmitting(true);
      if (!reportPurpose.trim()) {
        showToast("Please enter a purpose for this expense report", "error");
        setSubmitting(false);
        return;
      }
      await submitReport(report.ID, onBehalfOf, reportPurpose.trim());
      const forName = onBehalfOf ? ` (for ${onBehalfOf.name})` : "";
      showToast(`Report${forName} submitted for approval!`, "success");
      setReport(null);
      setLines([]);
      setOnBehalfOf(null);
      setReportPurpose("");
      setShowSubmitConfirm(false);
    } catch (e) {
      showToast(e.message || "Submission failed", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const onLineAdded = (newLine) => {
    setLines((prev) => [...prev, newLine]);
    if (!report) load();
  };

  const onLineUpdated = (updatedLine) => {
    setLines((prev) => prev.map((l) => l.ID === updatedLine.ID ? updatedLine : l));
  };

  const total = lines.reduce((sum, l) => sum + (Number(l.Amount) || 0), 0);

  const sortedLines = [...lines].sort((a, b) => {
    const da = a.ExpenseDate || "", db = b.ExpenseDate || "";
    return sortOrder === "asc" ? da.localeCompare(db) : db.localeCompare(da);
  });

  if (loading) {
    return <div className="loader"><div className="spinner" /></div>;
  }

  return (
    <div>
      {/* Summary */}
      <div className="summary-bar">
        <div>
          <div className="summary-label">Current Report Total</div>
          <div style={{ fontSize: 12, color: "var(--gray-4)", marginTop: 2 }}>
            {lines.length} expense{lines.length !== 1 ? "s" : ""}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
          <div className="summary-total">{formatCurrency(total)}</div>
          {lines.length > 1 && (
            <button
              onClick={() => setSortOrder((o) => o === "asc" ? "desc" : "asc")}
              style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: 12, color: "var(--gray-4)", padding: 0,
              }}
              title="Toggle sort order"
            >
              {sortOrder === "asc" ? "↑ Oldest first" : "↓ Newest first"}
            </button>
          )}
        </div>
      </div>

      {lines.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💸</div>
          <div className="empty-state-title">No expenses yet</div>
          <div className="empty-state-sub">
            Tap <strong>+ New Expense</strong> below to add your first item.
          </div>
        </div>
      ) : (
        <>
          {sortedLines.map((line) => {
            const isPendingDelete = confirmDeleteId === line.ID;
            return (
              <div
                key={line.ID}
                className="expense-card"
                style={isPendingDelete ? { background: "#fde7e9" } : {}}
              >
                <div className="expense-icon">{CATEGORY_ICONS[line.Category] || "🧾"}</div>
                <div
                  className="expense-info"
                  style={{ cursor: "pointer" }}
                  onClick={() => handleEditTap(line)}
                >
                  <div className="expense-category">{line.Category}</div>

                  <div className="expense-date">{formatDate(line.ExpenseDate)}</div>
                  {line.Description && (
                    <div style={{ fontSize: 12, color: "var(--gray-4)", marginTop: 2 }}>{line.Description}</div>
                  )}
                  {line.ReceiptUrl && (
                    <a
                      href={line.ReceiptUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="expense-receipt"
                      onClick={(e) => e.stopPropagation()}
                    >
                      📎 View receipt
                    </a>
                  )}
                </div>
                <div className="expense-right">
                  <div className="expense-amount">{formatCurrency(line.Amount)}</div>
                  {line.Category === "Mileage" && line.Miles && (
                    <div style={{ fontSize: 11, color: "var(--gray-4)", marginTop: 2 }}>
                      {line.Miles} mi
                    </div>
                  )}
                </div>
                <button
                  className="btn-delete"
                  onClick={() => handleDeleteTap(line.ID)}
                  title={isPendingDelete ? "Tap again to confirm removal" : "Remove"}
                  style={isPendingDelete ? { color: "var(--red)", fontWeight: 700 } : {}}
                >
                  {isPendingDelete ? "✕" : "×"}
                </button>
              </div>
            );
          })}

          <div style={{ marginTop: 8 }}>
            {showSubmitConfirm ? (
              <div className="submit-confirm-bar">
                {/* On-behalf-of selector — Debora only */}
                {isDelegate && (
                  <div style={{ marginBottom: 14 }}>
                    <label style={{
                      display: "block",
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--gray-4)",
                      letterSpacing: "0.06em",
                      marginBottom: 6,
                    }}>
                      SUBMITTING FOR
                    </label>
                    <select
                      value={onBehalfOf ? onBehalfOf.email : ""}
                      onChange={(e) => {
                        const opt = ON_BEHALF_OPTIONS.find((o) => o.email === e.target.value);
                        setOnBehalfOf(opt || null);
                      }}
                      style={{
                        width: "100%",
                        padding: "10px 14px",
                        borderRadius: 10,
                        border: "1.5px solid var(--gray-2)",
                        fontSize: 14,
                        background: "var(--gray-1)",
                        color: "var(--gray-6)",
                        outline: "none",
                      }}
                    >
                      <option value="">Myself (Debora Poynter)</option>
                      {ON_BEHALF_OPTIONS.map((o) => (
                        <option key={o.email} value={o.email}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Purpose for Expense Report */}
                <div style={{ marginBottom: 14 }}>
                  <label style={{
                    display: "block",
                    fontSize: 11,
                    fontWeight: 700,
                    color: "var(--gray-4)",
                    letterSpacing: "0.06em",
                    marginBottom: 6,
                  }}>
                    PURPOSE FOR EXPENSE REPORT <span style={{ color: "var(--red)" }}>*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Travel to Acme Corp — Q3 planning meeting"
                    value={reportPurpose}
                    onChange={(e) => setReportPurpose(e.target.value)}
                    style={{
                      width: "100%",
                      padding: "10px 14px",
                      borderRadius: 10,
                      border: "1.5px solid var(--gray-2)",
                      fontSize: 14,
                      background: "var(--gray-1)",
                      color: "var(--gray-6)",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8, color: "var(--gray-6)" }}>
                  Submit {lines.length} expense{lines.length !== 1 ? "s" : ""} ({formatCurrency(total)})
                  {onBehalfOf ? ` for ${onBehalfOf.name}` : ""} for approval?
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    className="btn btn-ghost"
                    style={{ flex: 1 }}
                    onClick={() => { setShowSubmitConfirm(false); setOnBehalfOf(null); setReportPurpose(""); }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-success"
                    style={{ flex: 2 }}
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? "Submitting…" : "Yes, Submit"}
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="btn btn-success"
                onClick={() => setShowSubmitConfirm(true)}
                disabled={lines.length === 0}
              >
                📤 Submit Report for Approval
              </button>
            )}
          </div>
        </>
      )}

      {showModal && (
        <AddExpenseModal
          user={user}
          reportId={report?.ID}
          editLine={editLine}
          onClose={() => { setShowModal(false); setEditLine(null); }}
          onAdded={(line) => {
            if (editLine) {
              onLineUpdated(line);
            } else {
              onLineAdded(line);
            }
            setShowModal(false);
            setEditLine(null);
          }}
        />
      )}
    </div>
  );
}
